/**
 * US-first phone entry, E.164 on the wire.
 *
 * The backend stores `ctms_subject_phi.phone_e164` in E.164 and the OTP
 * sender needs that form, but coordinators type US numbers the way they
 * read them off a chart — "(415) 555-1234", "415-555-1234", "4155551234".
 * Previously the field only accepted a literal `+1...` string and rejected
 * every one of those, which is the "phone number not US format" report.
 *
 * `normalisePhone` accepts either style and returns E.164, so international
 * numbers still work when entered with an explicit country code.
 */

/** E.164: leading +, country code 1-9, 7-15 digits total. */
export const E164_RE = /^\+[1-9]\d{6,14}$/;

/**
 * Convert user input to E.164, or return null when it isn't a number we
 * can resolve unambiguously.
 *
 *   "(415) 555-1234" → "+14155551234"
 *   "415-555-1234"   → "+14155551234"
 *   "1 415 555 1234" → "+14155551234"
 *   "+44 20 7946 0958" → "+442079460958"
 *   "555-1234"       → null  (7 digits — no area code)
 */
export function normalisePhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Anything typed with a leading + is treated as already-qualified;
  // strip formatting and validate as E.164.
  if (trimmed.startsWith('+')) {
    const compact = '+' + trimmed.slice(1).replace(/\D/g, '');
    return E164_RE.test(compact) ? compact : null;
  }

  const digits = trimmed.replace(/\D/g, '');
  // 10 digits → US/NANP without country code.
  if (digits.length === 10) return `+1${digits}`;
  // 11 digits starting with 1 → US/NANP with country code.
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/** Format an E.164 US number back to (AAA) NNN-NNNN for display. */
export function formatUsPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

export const PHONE_HINT =
  'Enter a US number, e.g. (415) 555-1234. For non-US numbers include the country code, e.g. +44 20 7946 0958.';
